import MuiButton from '@mui/material/Button';
import type { SxProps, Theme } from '@mui/material/styles';
import { alpha, darken, styled } from '@mui/material/styles';

const smoothEasing = 'cubic-bezier(0.4, 0, 0.2, 1)';
const buttonWideUppercaseLineHeight = 1.125;
const buttonComfortableLineHeight = 1.2;
const buttonCompactUppercaseLineHeight = 1.15;

export const buttonPrimarySx: SxProps<Theme> = {
  backgroundColor: (theme: Theme) => theme.palette.primary.main,
  borderRadius: '4px',
  boxShadow: 'none',
  color: (theme: Theme) => theme.palette.primary.contrastText,
  fontSize: 16,
  fontStyle: 'normal',
  fontWeight: 600,
  minHeight: 50,
  letterSpacing: '0.08em',
  lineHeight: buttonWideUppercaseLineHeight,
  paddingBottom: '16px',
  paddingLeft: '32px',
  paddingRight: '32px',
  paddingTop: '16px',
  textTransform: 'uppercase',
  transition: (theme: Theme) =>
    theme.transitions.create(['background-color', 'box-shadow', 'filter'], {
      duration: 280,
      easing: theme.transitions.easing.easeInOut,
    }),
  '&:hover': {
    backgroundColor: (theme: Theme) => darken(theme.palette.primary.main, 0.12),
    boxShadow: (theme: Theme) => `0 8px 28px ${alpha(theme.palette.primary.main, 0.48)}`,
    filter: 'none',
  },
  '&:active': {
    backgroundColor: (theme: Theme) => darken(theme.palette.primary.main, 0.2),
    boxShadow: (theme: Theme) => `0 4px 14px ${alpha(theme.palette.primary.main, 0.36)}`,
    filter: 'none',
  },
  '&[data-loading="true"].Mui-disabled': {
    backgroundColor: (theme: Theme) => theme.palette.action.disabledBackground,
    color: (theme: Theme) => theme.palette.action.disabled,
    opacity: 1,
  },
};

export const buttonPositiveSx: SxProps<Theme> = {
  ...buttonPrimarySx,
  backgroundColor: (theme: Theme) => theme.palette.secondary.main,
  color: (theme: Theme) => theme.palette.secondary.contrastText,
  '&:hover': {
    backgroundColor: (theme: Theme) => darken(theme.palette.secondary.main, 0.12),
    boxShadow: (theme: Theme) => `0 8px 28px ${alpha(theme.palette.secondary.main, 0.48)}`,
    filter: 'none',
  },
  '&:active': {
    backgroundColor: (theme: Theme) => darken(theme.palette.secondary.main, 0.2),
    boxShadow: (theme: Theme) => `0 4px 14px ${alpha(theme.palette.secondary.main, 0.36)}`,
    filter: 'none',
  },
  '&[data-loading="true"].Mui-disabled': {
    backgroundColor: (theme: Theme) => theme.palette.action.disabledBackground,
    color: (theme: Theme) => theme.palette.action.disabled,
    opacity: 1,
  },
};

export const buttonNeutralOutlineSx: SxProps<Theme> = {
  backgroundColor: (theme: Theme) => theme.palette.background.paper,
  borderColor: (theme: Theme) => theme.palette.grey[300],
  borderRadius: '4px',
  borderStyle: 'solid',
  borderWidth: 1,
  color: 'common.black',
  fontSize: 16,
  fontStyle: 'normal',
  fontWeight: 400,
  letterSpacing: 0,
  lineHeight: buttonComfortableLineHeight,
  paddingBottom: '17px',
  paddingLeft: (theme: Theme) => theme.spacing(1.5),
  paddingRight: (theme: Theme) => theme.spacing(1.5),
  paddingTop: '17px',
  textTransform: 'none',
  transition: (theme: Theme) =>
    theme.transitions.create(['background-color', 'border-color'], {
      duration: 280,
      easing: theme.transitions.easing.easeInOut,
    }),
  '&:hover': {
    backgroundColor: (theme: Theme) => theme.palette.grey[50],
    borderColor: (theme: Theme) => theme.palette.grey[400],
  },
  '&:active': {
    backgroundColor: (theme: Theme) => theme.palette.grey[100],
    borderColor: (theme: Theme) => theme.palette.grey[400],
  },
  '& .MuiButton-startIcon': {
    marginLeft: 0,
    marginRight: '8px',
  },
  '& .MuiButton-startIcon > *:first-of-type': {
    display: 'block',
    height: 24,
    width: 24,
  },
};

export const buttonPrimaryOutlineSx: SxProps<Theme> = {
  backgroundColor: (theme: Theme) => theme.palette.background.paper,
  borderColor: (theme: Theme) => theme.palette.primary.main,
  borderRadius: '4px',
  borderStyle: 'solid',
  borderWidth: 1,
  color: (theme: Theme) => theme.palette.primary.main,
  fontSize: 16,
  fontStyle: 'normal',
  fontWeight: 600,
  letterSpacing: '0.08em',
  lineHeight: buttonWideUppercaseLineHeight,
  minHeight: 50,
  paddingBottom: '16px',
  paddingLeft: '32px',
  paddingRight: '32px',
  paddingTop: '16px',
  textTransform: 'uppercase',
  transition: (theme: Theme) =>
    theme.transitions.create(['background-color', 'border-color', 'box-shadow'], {
      duration: 280,
      easing: theme.transitions.easing.easeInOut,
    }),
  '&:hover': {
    backgroundColor: (theme: Theme) => alpha(theme.palette.primary.main, 0.08),
    borderColor: (theme: Theme) => theme.palette.primary.main,
    boxShadow: 'none',
    color: (theme: Theme) => theme.palette.primary.main,
  },
  '&:active': {
    backgroundColor: (theme: Theme) => alpha(theme.palette.primary.main, 0.14),
    borderColor: (theme: Theme) => theme.palette.primary.main,
    boxShadow: 'none',
    color: (theme: Theme) => theme.palette.primary.main,
  },
};

export const buttonPrimaryGhostSx: SxProps<Theme> = {
  backgroundColor: 'transparent',
  borderRadius: '4px',
  boxShadow: 'none',
  color: (theme: Theme) => theme.palette.primary.main,
  filter: 'none',
  fontSize: 14,
  fontWeight: 700,
  letterSpacing: 0,
  lineHeight: buttonCompactUppercaseLineHeight,
  minHeight: 40,
  paddingBottom: '12px',
  paddingLeft: '16px',
  paddingRight: '16px',
  paddingTop: '12px',
  textTransform: 'uppercase',
  transition: (theme: Theme) =>
    theme.transitions.create(['background-color', 'box-shadow', 'filter'], {
      duration: 220,
      easing: theme.transitions.easing.easeInOut,
    }),
  '&:hover': {
    backgroundColor: (theme: Theme) => alpha(theme.palette.primary.main, 0.06),
    boxShadow: 'none',
    filter: 'none',
  },
  '&:active': {
    backgroundColor: (theme: Theme) => alpha(theme.palette.primary.main, 0.1),
    boxShadow: 'none',
    filter: 'none',
  },
  '&.Mui-disabled': {
    backgroundColor: 'transparent',
    boxShadow: 'none',
    color: 'rgba(0, 0, 0, 0.38)',
    filter: 'none',
    opacity: 1,
  },
};

export const buttonCompactSx: SxProps<Theme> = {
  borderRadius: '4px',
  boxShadow: 'none',
  filter: 'none',
  fontSize: 14,
  fontWeight: 700,
  letterSpacing: 0,
  lineHeight: buttonCompactUppercaseLineHeight,
  minHeight: 40,
  paddingBottom: '12px',
  paddingLeft: '16px',
  paddingRight: '16px',
  paddingTop: '12px',
  textTransform: 'uppercase',
  '&.MuiButton-contained, &.MuiButton-outlined, &.MuiButton-text': {
    boxShadow: 'none',
    filter: 'none',
  },
  '&.MuiButton-contained:not(.Mui-disabled), &.MuiButton-outlined:not(.Mui-disabled), &.MuiButton-text:not(.Mui-disabled)': {
    boxShadow: 'none',
    filter: 'none',
    '&:hover, &:active': {
      boxShadow: 'none',
      filter: 'none',
    },
  },
  '&.Mui-disabled': {
    boxShadow: 'none',
    filter: 'none',
    opacity: 1,
  },
};

export const StyledButton = styled(MuiButton)(({ theme }) => ({
  minHeight: 48,
  borderRadius: 14,
  textTransform: 'none',
  fontWeight: 700,
  boxShadow: 'none',
  transition: theme.transitions.create(['background-color', 'color', 'border-color', 'box-shadow', 'filter'], {
    duration: 280,
    easing: smoothEasing,
  }),
  '&:hover': {
    boxShadow: 'none',
  },
  '&.MuiButton-contained:not(.Mui-disabled)': {
    transition: theme.transitions.create(['background-color', 'color', 'box-shadow', 'filter'], {
      duration: 320,
      easing: smoothEasing,
    }),
    '&:hover': {
      boxShadow: '0 6px 18px rgba(0, 0, 0, 0.14)',
      filter: 'brightness(0.91)',
    },
    '&:active': {
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.12)',
      filter: 'brightness(0.86)',
      transitionDuration: '140ms',
    },
  },
  '&.MuiButton-outlined:not(.Mui-disabled)': {
    transition: theme.transitions.create(['background-color', 'color', 'border-color', 'box-shadow'], {
      duration: 280,
      easing: smoothEasing,
    }),
    '&:hover, &:active': {
      filter: 'none',
    },
    '&:active': {
      transitionDuration: '140ms',
    },
  },
  '&.Mui-disabled': {
    filter: 'none',
    opacity: 0.7,
  },
  '&:focus-visible': {
    outline: `3px solid ${theme.palette.primary.light}`,
    outlineOffset: 2,
  },
})) as typeof MuiButton;
